import React, { FC, useEffect } from 'react';
import { useTranslation } from '@wix/wix-i18n-config';

import { TemplatesGrid } from '../TemplatesGrid/TemplatesGrid';
import { InspirationalTemplatesPage } from './InspirationalTemplatesPage/InspirationalTemplatesPage';
import { TemplatePreviewModal } from '../TemplatePreviewModal/TemplatePreviewModal';
import { SubHeaderWithSearch } from '../SubHeaderWithSearch/SubHeaderWithSearch';
import { TopBar } from '../TopBar/TopBar';
import { BottomNavigationMenu } from '../BottomNavigationMenu/BottomNavigationMenu';
import { Footer } from '../Footer/Footer';
import { Pagination } from '../Pagination/Pagination';
import { useSelectedCategory } from '../../hooks/useSelectedCategory';
import { useSearch } from '../../hooks/useSearch';
import { useBiLogger } from '../../contexts/BILoggerContext';
import { useExperiments } from '../../contexts/ExperimentsContext';
import { usePageScroll } from '../../contexts/PageScrollContext';
import { CategorySlug } from '../../config';
import { templatesPageDataHooks } from './TemplatesPage.dataHooks';

import s from './TemplatesPage.scss';
import cs from 'classnames';
import { useTemplates } from '../../contexts/TemplatesContext';

interface TemplatesPageProps {}

const TemplatesPage: FC<TemplatesPageProps> = () => {
  const { templates, error, loading } = useTemplates();
  const biLogger = useBiLogger();
  const { t } = useTranslation();
  const { isSearchActive, criteria } = useSearch();
  const selectedCategory = useSelectedCategory();
  const isInspirationalTemplates =
    selectedCategory?.slug === CategorySlug.INSPIRATIONAL;
  const { is } = useExperiments();
  const { topPageRef } = usePageScroll();
  const isEmptyResult = !error && !loading && templates.length === 0;

  const isTopBarSecondPhase = is(
    'specs.funnel.StudioTemplatesTopBarSecondPhaseV1',
    'true',
  );

  useEffect(() => {
    biLogger.reportTemplatesPageView();
  }, [selectedCategory?.slug, criteria]); // we should report page view on category change or criteria change

  let heading = '';

  if (selectedCategory) {
    heading = t('app.new.title.category', {
      category: selectedCategory.displayName,
    });

    const isHomePage = selectedCategory.slug === CategorySlug.DEFAULT;

    if (isHomePage) {
      heading = t('app.new.title.home');
    } else if (selectedCategory.slug === CategorySlug.DESIGNED_TEMPLATES) {
      heading = t('app.new.title.designed');
    }
  }

  if (isSearchActive) {
    heading = t('app.new.title.search', { criteria });
  }

  return (
    <div className={cs({ 'top-bar-phase2-enabled': isTopBarSecondPhase })}>
      <TemplatePreviewModal />
      <div className={s.subHeaderWithTopBar}>
        <SubHeaderWithSearch />
      </div>
      <TopBar />
      <div ref={topPageRef} className={s.topTarget} />
      <div className={s.content}>
        <BottomNavigationMenu />
        <main data-hook={templatesPageDataHooks.container()} className={s.main}>
          {isInspirationalTemplates ? (
            <InspirationalTemplatesPage
              heading={selectedCategory!.displayName}
            />
          ) : (
            <TemplatesGrid heading={heading} />
          )}
          {!isEmptyResult && <Pagination />}
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default TemplatesPage;
